import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});

onDocumentLoaded(() => {
  const hero = document.querySelector('.drpepper-home-hero-section');
  const visualToggle = hero?.querySelector('[data-drpepper-mobile-menu-toggle]');

  if (!(hero instanceof HTMLElement) || !(visualToggle instanceof HTMLElement)) return;
  if (hero.hasAttribute('data-drpepper-mobile-menu-version')) return;
  if (visualToggle.dataset.drpepperRuntimeDrawerBound === 'true') return;

  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const existingShell = hero.querySelector('[data-drpepper-runtime-drawer-shell]');
  if (existingShell) return;

  visualToggle.dataset.drpepperRuntimeDrawerBound = 'true';

  if (!document.getElementById('drpepper-mobile-menu-runtime-style')) {
    const style = document.createElement('style');
    style.id = 'drpepper-mobile-menu-runtime-style';
    style.textContent = `
      @media screen and (max-width: 749px) {
        .drpepper-runtime-mobile-drawer-backdrop {
          position: fixed;
          inset: 0;
          border: 0;
          background: rgba(11, 10, 10, 0.4);
          opacity: 0;
          pointer-events: none;
          z-index: 39;
          transition: opacity 0.24s ease;
        }

        .drpepper-runtime-mobile-drawer {
          position: fixed;
          top: 0;
          left: 0;
          width: min(370px, 94vw);
          height: 100dvh;
          padding: 20px 18px 28px;
          box-sizing: border-box;
          background: #ffffff;
          color: #120e0e;
          box-shadow: 0 24px 72px rgba(0, 0, 0, 0.24);
          transform: translateX(-100%);
          opacity: 0;
          pointer-events: none;
          z-index: 40;
          transition: transform 0.28s ease, opacity 0.28s ease;
        }

        .drpepper-runtime-mobile-drawer__close {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          margin-left: auto;
          border: 0;
          background: transparent;
          color: #120e0e;
          cursor: pointer;
          padding: 0;
          font-size: 32px;
          line-height: 1;
        }

        .drpepper-runtime-mobile-drawer__list {
          list-style: none;
          margin: 18px 0 0;
          padding: 0;
        }

        .drpepper-runtime-mobile-drawer__item + .drpepper-runtime-mobile-drawer__item {
          border-top: 1px solid rgba(18, 14, 14, 0.12);
        }

        .drpepper-runtime-mobile-drawer__link {
          display: flex;
          align-items: center;
          min-height: 68px;
          color: #120e0e;
          text-decoration: none;
          font-family: "aktiv-grotesk-extended", sans-serif;
          font-size: 26px;
          line-height: 1.08;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0;
        }

        .drpepper-runtime-mobile-drawer__link:hover,
        .drpepper-runtime-mobile-drawer__link:focus-visible,
        .drpepper-runtime-mobile-drawer__link[aria-current='page'] {
          color: #900d2c;
        }

        .drpepper-runtime-mobile-drawer__link:focus-visible,
        .drpepper-runtime-mobile-drawer__close:focus-visible {
          outline: 3px solid #900d2c;
          outline-offset: 3px;
        }

        .drpepper-home-hero-section[data-drpepper-runtime-drawer-open='true'] .drpepper-runtime-mobile-drawer-backdrop {
          opacity: 1;
          pointer-events: auto;
        }

        .drpepper-home-hero-section[data-drpepper-runtime-drawer-open='true'] .drpepper-runtime-mobile-drawer {
          transform: translateX(0);
          opacity: 1;
          pointer-events: auto;
        }
      }
    `;
    document.head.appendChild(style);
  }

  const menuLinks = [
    ...document.querySelectorAll('#Details-menu-drawer-container .menu-drawer__menu-item--mainlist'),
  ]
    .map((link) => {
      if (!(link instanceof HTMLAnchorElement)) return null;
      return {
        href: link.href,
        title: link.textContent?.trim() || '',
        current: link.getAttribute('aria-current') === 'page',
      };
    })
    .filter((item) => item && item.title);

  const links = menuLinks.length > 0 ? menuLinks : [
    { href: '/', title: 'Home', current: window.location.pathname === '/' },
    { href: '/collections/all', title: 'Products', current: window.location.pathname === '/collections/all' },
  ];

  const shell = document.createElement('div');
  shell.setAttribute('data-drpepper-runtime-drawer-shell', 'true');
  shell.setAttribute('aria-hidden', 'true');
  shell.innerHTML = `
    <button
      class="drpepper-runtime-mobile-drawer-backdrop"
      type="button"
      aria-label="Close mobile menu"
      data-drpepper-runtime-close
    ></button>
    <nav
      class="drpepper-runtime-mobile-drawer"
      aria-label="Mobile menu"
    >
      <button
        class="drpepper-runtime-mobile-drawer__close"
        type="button"
        aria-label="Close mobile menu"
        data-drpepper-runtime-close
      >&times;</button>
      <ul class="drpepper-runtime-mobile-drawer__list" role="list">
        ${links
          .map(
            (link) => `
              <li class="drpepper-runtime-mobile-drawer__item">
                <a
                  class="drpepper-runtime-mobile-drawer__link"
                  href="${link.href}"
                  ${link.current ? 'aria-current="page"' : ''}
                  data-drpepper-runtime-link
                >
                  <span>${link.title}</span>
                </a>
              </li>
            `
          )
          .join('')}
      </ul>
    </nav>
  `;
  hero.appendChild(shell);

  const syncOverflow = (open) => {
    document.documentElement.style.overflow = open ? 'hidden' : '';
    document.body.style.overflow = open ? 'hidden' : '';
  };

  const setOpen = (open) => {
    hero.dataset.drpepperRuntimeDrawerOpen = open ? 'true' : 'false';
    shell.setAttribute('aria-hidden', open ? 'false' : 'true');
    syncOverflow(open);
  };

  visualToggle.addEventListener('click', (event) => {
    if (!mobileQuery.matches) return;

    event.preventDefault();
    event.stopPropagation();
    setOpen(hero.dataset.drpepperRuntimeDrawerOpen !== 'true');
  });

  shell.querySelectorAll('[data-drpepper-runtime-close], [data-drpepper-runtime-link]').forEach((node) => {
    node.addEventListener('click', () => {
      setOpen(false);
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && hero.dataset.drpepperRuntimeDrawerOpen === 'true') {
      setOpen(false);
    }
  });

  const closeOnDesktop = (event) => {
    if (event?.matches === false || !mobileQuery.matches) {
      setOpen(false);
    }
  };

  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', closeOnDesktop);
  } else if (typeof mobileQuery.addListener === 'function') {
    mobileQuery.addListener(closeOnDesktop);
  }
});
